import { Grid } from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  CreateOrganizationAddressInput,
  CreateOrganizationAddressMutationResult,
} from "generated/graphql";
import useCreateOrganizationAddress from "hooks/organization/useCreateOrganizationAddress";
import DashboardLayout from "layouts/DashboardLayout/DashboardLayout";
import Create from "modules/OrganizationAddresses/Create";
import DashboardNavbar from "partials/DashboardNavbar/DashboardNavbar";
import Footer from "partials/Footer/Footer";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { getRoute } from "utils/routing";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import TourHelpButton from "components/PageTour/TourHelpButton";
import createAddressPageTourSteps from "./create.tour";

export default function OrganizationAddressCreatePage() {
  const [createOrganizationAddress, { loading, error, isSuccess, getData }] =
    useCreateOrganizationAddress();
  const navigate = useNavigate();
  const location = useLocation();
  const tourRef = useRef<PageTourHandle>(null);

  useEffect(() => {
    if ((location.state as { startTour?: boolean })?.startTour) {
      tourRef.current?.replay();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const onSubmit = useCallback(
    async (input: CreateOrganizationAddressInput) => {
      const result = await createOrganizationAddress({ variables: { input } });
      const success = isSuccess(result.data);
      const data = getData(result);
      // Let's send the user to the address list
      if (isSuccess) {
        navigate(getRoute("organization-settings.organization.settings"));
      }
      return { success, data };
    },
    [createOrganizationAddress]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageTour ref={tourRef} tourKey="createAddressPage" steps={createAddressPageTourSteps} />
      {error && <MDAlert color="error">Failed to create address</MDAlert>}
      <MDBox my={3}>
        <MDBox mb={6}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={6}>
              <MDTypography
                variant="h4"
                fontWeight="medium"
                display="flex"
                alignItems="center"
                gap={1}
              >
                Create Address
                <TourHelpButton
                  tooltip="Click here to learn more about Creating an Address"
                  onClick={() => tourRef.current?.replay()}
                />
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Create
              onSubmit={onSubmit}
              loading={loading}
              onAddressSelected={() => tourRef.current?.next()}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
